/** Canonical study_parts population field keys (form, DB, Gemini parse, protocol prompts). */
export const BRIEF_DESCRIPTION_OF_TRIAL_POPULATION = 'brief_description_of_trial_population';
export const GENERAL_DESCRIPTION_OF_TRIAL_POPULATION = 'general_description_of_trial_population';

/** @deprecated Legacy keys — migrated on load/save */
export const LEGACY_PATIENT_POPULATION = 'patient_population';
export const LEGACY_SHORT_DESCRIPTION = 'short_description';
export const LEGACY_LONG_DESCRIPTION = 'long_description';

export const STUDY_PARTS_POPULATION_FIELD_LABELS = {
  [BRIEF_DESCRIPTION_OF_TRIAL_POPULATION]: 'Brief Description of Trial Population',
  [GENERAL_DESCRIPTION_OF_TRIAL_POPULATION]: 'General Description of Trial Population',
};

/** Variable paths for protocol / Gemini prompts */
export const STUDY_PARTS_POPULATION_VARIABLE_PATHS = {
  [BRIEF_DESCRIPTION_OF_TRIAL_POPULATION]:
    'study_overview.study_parts[part][cohort].brief_description_of_trial_population',
  [GENERAL_DESCRIPTION_OF_TRIAL_POPULATION]:
    'study_overview.study_parts[part][cohort].general_description_of_trial_population',
};

export const STUDY_PARTS_POPULATION_FIELD_KEYS = [
  BRIEF_DESCRIPTION_OF_TRIAL_POPULATION,
  GENERAL_DESCRIPTION_OF_TRIAL_POPULATION,
];

/** M11 Section 4 — Committees on the study-parts matrix (per part/cohort). */
export const STUDY_PARTS_COMMITTEES_FIELD = 'committees';
export const STUDY_PARTS_COMMITTEES_VARIABLE_PATH =
  'study_overview.study_parts[part][cohort].committees';

export const isNonemptyStudyPartCell = (value) => {
  const trimmed = value == null ? '' : String(value).trim();
  return trimmed !== '' && trimmed !== 'Not Provided';
};

export const firstStudyPartsFieldValue = (studyParts, fieldKey) => {
  if (!studyParts || typeof studyParts !== 'object') return '';
  const partKeys = Object.keys(studyParts).sort(
    (a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10)
  );
  for (const partKey of partKeys) {
    const part = studyParts[partKey];
    if (!part || typeof part !== 'object') continue;
    for (const cohortKey of Object.keys(part).sort()) {
      const cell = part[cohortKey];
      if (!cell || typeof cell !== 'object') continue;
      const val = cell[fieldKey];
      if (isNonemptyStudyPartCell(val)) return String(val).trim();
    }
  }
  return '';
};

export const normalizeRegulatoryCommitteesValue = (committees) => {
  if (Array.isArray(committees)) {
    return committees.filter((item) => item != null && String(item).trim() !== '').join('\n');
  }
  return committees == null ? '' : String(committees).trim();
};

/** Prefer study_parts matrix value; fall back to regulatory_requirements.committees. */
export const resolveCommitteesForIntake = (studyParts, regulatoryCommittees) => {
  const fromParts = firstStudyPartsFieldValue(studyParts, STUDY_PARTS_COMMITTEES_FIELD);
  if (fromParts) return fromParts;
  const fromRegulatory = normalizeRegulatoryCommitteesValue(regulatoryCommittees);
  return fromRegulatory && fromRegulatory !== 'Not Provided' ? fromRegulatory : '';
};

export const backfillStudyPartsCommitteesFromRegulatory = (studyParts, regulatoryCommittees) => {
  if (firstStudyPartsFieldValue(studyParts, STUDY_PARTS_COMMITTEES_FIELD)) {
    return studyParts;
  }
  const committees = resolveCommitteesForIntake(null, regulatoryCommittees);
  if (!committees) return studyParts;

  const updated =
    studyParts && typeof studyParts === 'object'
      ? JSON.parse(JSON.stringify(studyParts))
      : {};
  const partKeys = Object.keys(updated).sort(
    (a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10)
  );
  const targetPartKey = partKeys[0] || 'part1';
  if (!updated[targetPartKey] || typeof updated[targetPartKey] !== 'object') {
    updated[targetPartKey] = {};
  }
  const part = updated[targetPartKey];
  if (part.direct && typeof part.direct === 'object') {
    part.direct[STUDY_PARTS_COMMITTEES_FIELD] = committees;
  } else {
    Object.keys(part).forEach((cohortKey) => {
      if (part[cohortKey] && typeof part[cohortKey] === 'object') {
        part[cohortKey][STUDY_PARTS_COMMITTEES_FIELD] = committees;
      }
    });
  }
  return updated;
};

export const studyPartsTrialDesignGeminiPromptLines = () =>
  `- ${STUDY_PARTS_COMMITTEES_VARIABLE_PATH} (Committees — oversight committees e.g. DMC, DSMB, dose escalation; include brief role description)`;

export const migrateStudyPartsCohortPopulationKeys = (cohort) => {
  if (!cohort || typeof cohort !== 'object') return cohort;

  if (
    cohort[LEGACY_PATIENT_POPULATION] != null &&
    cohort[BRIEF_DESCRIPTION_OF_TRIAL_POPULATION] == null
  ) {
    cohort[BRIEF_DESCRIPTION_OF_TRIAL_POPULATION] = cohort[LEGACY_PATIENT_POPULATION];
  }
  delete cohort[LEGACY_PATIENT_POPULATION];

  if (
    cohort[LEGACY_SHORT_DESCRIPTION] != null &&
    cohort[GENERAL_DESCRIPTION_OF_TRIAL_POPULATION] == null
  ) {
    cohort[GENERAL_DESCRIPTION_OF_TRIAL_POPULATION] = cohort[LEGACY_SHORT_DESCRIPTION];
  }
  delete cohort[LEGACY_SHORT_DESCRIPTION];

  delete cohort[LEGACY_LONG_DESCRIPTION];

  return cohort;
};

export const migrateStudyPartsPopulationKeys = (studyParts) => {
  if (!studyParts || typeof studyParts !== 'object') return studyParts;
  Object.values(studyParts).forEach((part) => {
    if (!part || typeof part !== 'object') return;
    Object.values(part).forEach((cohort) => migrateStudyPartsCohortPopulationKeys(cohort));
  });
  return studyParts;
};
